import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["title", "timeline", "duration", "description", "guideline", "status", "submitButton"]
  static values = { saveUrl: String }

  connect() {
    this.saveTimeout = null
  }

  disconnect() {
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout)
    }
  }

  scheduleUpdate() {
    // Update the activity title in the card header when name changes
    if (this.hasTitleTarget) {
      this.updateActivityTitle(this.titleTarget.value)
    }

    // No auto-save - only save when button is clicked
  }

  updateActivityTitle(newTitle) {
    // Find the activity card title element and update it
    const activityCard = this.element.closest('[data-activity-card]')
    if (activityCard) {
      const titleElement = activityCard.querySelector('h4')
      if (titleElement) {
        titleElement.textContent = newTitle
      }
      activityCard.dataset.activityTitle = newTitle
    }
  }

  updateModalTitles(newTitle) {
    if (!newTitle) return

    // Find all modal title elements and update them
    const modalTitleElements = document.querySelectorAll('[data-activity-title-modal]')
    modalTitleElements.forEach(el => {
      const type = el.dataset.activityTitleModal

      if (type === 'placeholder') {
        // Update placeholder attribute (e.g., delete confirmation input)
        el.placeholder = `Type '${newTitle}' to confirm`
      } else if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
        // Update input value (e.g., save to catalog title)
        el.value = newTitle
      } else {
        // Update text content for regular elements
        el.textContent = newTitle
      }
    })
  }

  async save(event) {
    // Get the submit button
    const submitButton = this.hasSubmitButtonTarget ? this.submitButtonTarget : event?.target?.closest('button')

    if (!submitButton) {
      console.error("No submit button found")
      return
    }

    const originalButtonText = submitButton.textContent.trim()

    // Disable button while saving
    submitButton.disabled = true

    // Get timeline value from hidden input created by date picker
    const timelineInput = this.element.querySelector('input[name="configuration[timeline]"]')

    // Collect selected activities from file-upload controller (activity chips)
    const activityChips = this.element.querySelectorAll('[data-activity-chip]')
    const selectedActivityIds = []
    activityChips.forEach(chip => {
      const activityId = chip.dataset.activityId
      if (activityId) {
        selectedActivityIds.push(activityId)
      }
    })

    const data = {
      activity: {
        title: this.hasTitleTarget ? this.titleTarget.value : undefined
      },
      configuration: {
        timeline: timelineInput ? timelineInput.value : undefined,
        duration: this.hasDurationTarget ? this.durationTarget.value : undefined,
        description: this.hasDescriptionTarget ? this.descriptionTarget.value : undefined,
        guideline: this.hasGuidelineTarget ? this.guidelineTarget.value : undefined,
        input_activity_ids: selectedActivityIds
      }
    }

    try {
      const response = await fetch(this.saveUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        const activityCard = this.element.closest('[data-activity-card]')
        const activityId = activityCard?.dataset?.activityId
        const newTitle = this.hasTitleTarget ? this.titleTarget.value : undefined

        // Update modal titles that contain the activity name
        this.updateModalTitles(newTitle)

        window.dispatchEvent(new CustomEvent('activity-list-changed', {
          detail: { type: 'update', activityId: activityId }
        }))

        submitButton.textContent = 'Saved'
        // Re-enable and restore text after 2 seconds
        setTimeout(() => {
          submitButton.disabled = false
          submitButton.textContent = originalButtonText
        }, 2000)
      } else {
        const error = await response.json()
        console.error('Save error:', error)
        this.showStatus("Error: " + (error.errors?.join(', ') || 'Save failed'), true)

        submitButton.disabled = false
        submitButton.textContent = originalButtonText
      }
    } catch (error) {
      console.error('Save error:', error)
      this.showStatus("Error saving", true)

      submitButton.disabled = false
      submitButton.textContent = originalButtonText
    }
  }

  showStatus(message, isError = false) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      this.statusTarget.classList.toggle("text-red-600", isError)
    }
  }

  clearStatus() {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = ""
    }
  }
}
